import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { Table, Zone, Location } from '../models';
import { TableStatus } from '../enums';
import { DatabaseService } from './database.service';
import { map, tap, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TableService {
  private tablesSubject = new BehaviorSubject<Table[]>([]);
  private zonesSubject = new BehaviorSubject<Zone[]>([]);
  private locationsSubject = new BehaviorSubject<Location[]>([]);
  private selectedLocationIdSubject = new BehaviorSubject<string | null>(null);

  public tables$: Observable<Table[]> = this.tablesSubject.asObservable();
  public zones$: Observable<Zone[]> = this.zonesSubject.asObservable();
  public locations$: Observable<Location[]> = this.locationsSubject.asObservable();
  public selectedLocationId$: Observable<string | null> = this.selectedLocationIdSubject.asObservable();

  constructor(private database: DatabaseService) {
    // Esperar un poco para que IndexedDB esté listo
    setTimeout(() => {
      this.loadData();
    }, 300);
  }

  private loadData(): void {
    // Cargar mesas desde IndexedDB
    this.database.getAllTables().subscribe({
      next: (tables) => {
        const tableObjects = tables.map(t => this.deserializeTable(t));
        this.tablesSubject.next(tableObjects);
      },
      error: (error) => {
        console.error('Error loading tables:', error);
        this.tablesSubject.next([]);
      }
    });

    // Cargar zonas desde IndexedDB
    this.database.getAllZones().subscribe({
      next: (zones) => {
        const zoneObjects = zones.map(z => this.deserializeZone(z));
        this.zonesSubject.next(zoneObjects);
      },
      error: (error) => {
        console.error('Error loading zones:', error);
        this.zonesSubject.next([]);
      }
    });

    // Cargar sedes (locations)
    this.database.getAllLocations().subscribe({
      next: (locations) => {
        const list = (locations || []).map((l: any) => this.deserializeLocation(l));
        if (list.length === 0) {
          const defaultLocation = new Location('location_main', 'Sede Principal');
          this.createLocation(defaultLocation);
        } else {
          this.locationsSubject.next(list);
          if (!this.selectedLocationIdSubject.value) {
            this.selectedLocationIdSubject.next(list[0].id);
          }
        }
      },
      error: (error) => {
        console.error('Error loading locations:', error);
        this.locationsSubject.next([]);
      }
    });
  }

  private deserializeLocation(data: any): Location {
    const loc = new Location(data.id, data.name, data.address);
    loc.active = data.active !== undefined ? data.active : true;
    return loc;
  }

  private serializeLocation(location: Location): any {
    return {
      id: location.id,
      name: location.name,
      address: location.address,
      active: location.active !== undefined ? location.active : true
    };
  }

  private deserializeTable(data: any): Table {
    const table = new Table(
      data.id,
      data.number,
      data.capacity,
      data.zoneId,
      data.positionX || 0,
      data.positionY || 0
    );
    table.status = data.status || TableStatus.AVAILABLE;
    table.groupId = data.groupId;
    table.linkedTables = data.linkedTables;
    table.active = data.active !== undefined ? data.active : true;
    table.accessCode = data.accessCode ?? this.generateRandomAccessCode();
    table.createdAt = data.createdAt ? new Date(data.createdAt) : new Date();
    table.updatedAt = data.updatedAt ? new Date(data.updatedAt) : new Date();
    return table;
  }

  /** Genera un código aleatorio para acceso por QR (no contiene datos de la mesa). */
  generateRandomAccessCode(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID().replace(/-/g, '');
    }
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 16; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }

  private deserializeZone(data: any): Zone {
    const zone = new Zone(data.id, data.name, data.type);
    zone.description = data.description;
    zone.locationId = data.locationId ?? null;
    zone.active = data.active !== undefined ? data.active : true;
    // Migrar datos antiguos si existen
    if (data.doorPosition && !data.doors) {
      zone.doors = [{ id: `door-${data.id}-0`, x: data.doorPosition.x, y: data.doorPosition.y }];
    } else {
      zone.doors = data.doors || [];
    }
    if (data.kitchenPosition && !data.kitchens) {
      zone.kitchens = [{ id: `kitchen-${data.id}-0`, x: data.kitchenPosition.x, y: data.kitchenPosition.y }];
    } else {
      zone.kitchens = data.kitchens || [];
    }
    return zone;
  }

  private serializeTable(table: Table): any {
    return {
      id: table.id,
      number: table.number,
      capacity: table.capacity,
      status: table.status,
      zoneId: table.zoneId,
      positionX: table.positionX,
      positionY: table.positionY,
      groupId: table.groupId,
      linkedTables: table.linkedTables,
      active: table.active !== undefined ? table.active : true,
      accessCode: table.accessCode ?? this.generateRandomAccessCode(),
      createdAt: table.createdAt.toISOString(),
      updatedAt: table.updatedAt.toISOString()
    };
  }

  private serializeZone(zone: Zone): any {
    return {
      id: zone.id,
      name: zone.name,
      type: zone.type,
      description: zone.description,
      locationId: zone.locationId ?? null,
      doors: zone.doors || [],
      kitchens: zone.kitchens || [],
      active: zone.active !== undefined ? zone.active : true
    };
  }

  getTables(): Table[] {
    return this.tablesSubject.value.filter(t => t.active !== false);
  }

  /** Todas las mesas (incluidas inactivas) para lógica interna. */
  getAllTablesRaw(): Table[] {
    return this.tablesSubject.value;
  }

  getTableById(id: string): Table | undefined {
    return this.getTables().find(table => table.id === id);
  }

  /** Busca mesa por código de acceso QR (cualquier mesa, activa o no). */
  getTableByAccessCode(code: string): Table | undefined {
    if (!code?.trim()) return undefined;
    return this.tablesSubject.value.find(t => t.accessCode === code.trim());
  }

  getTablesByZone(zoneId: string): Table[] {
    return this.getTables().filter(table => table.zoneId === zoneId);
  }

  createTable(table: Table): Table {
    if (!table.accessCode?.trim()) {
      table.accessCode = this.generateRandomAccessCode();
    }
    const serialized = this.serializeTable(table);
    this.database.createTable(serialized).subscribe({
      next: () => {
        const tables = [...this.getTables(), table];
        this.tablesSubject.next(tables);
      },
      error: (error) => {
        console.error('Error creating table:', error);
      }
    });
    return table;
  }

  updateTable(table: Table): Table {
    table.updatedAt = new Date();
    const serialized = this.serializeTable(table);
    this.database.updateTable(serialized).subscribe({
      next: () => {
        const tables = this.getTables();
        const index = tables.findIndex(t => t.id === table.id);
        if (index !== -1) {
          tables[index] = table;
          this.tablesSubject.next([...tables]);
        }
      },
      error: (error) => {
        console.error('Error updating table:', error);
      }
    });
    return table;
  }

  deleteTable(id: string): void {
    this.database.deleteTable(id).subscribe({
      next: () => {
        const tables = this.getTables().filter(t => t.id !== id);
        this.tablesSubject.next(tables);
      },
      error: (error) => {
        console.error('Error deleting table:', error);
      }
    });
  }

  updateTableStatus(tableId: string, status: TableStatus): void {
    const table = this.getTableById(tableId);
    if (table) {
      table.status = status;
      table.updatedAt = new Date();
      this.updateTable(table);
    }
  }

  /** Actualiza estado de mesa solo en memoria (simulador); no persiste. */
  updateTableStatusSimulation(tableId: string, status: TableStatus): void {
    const tables = this.tablesSubject.value;
    const table = tables.find(t => t.id === tableId);
    if (table) {
      table.status = status;
      table.updatedAt = new Date();
      this.tablesSubject.next([...tables]);
    }
  }

  /** Recarga mesas desde la base de datos (p. ej. tras terminar simulador). */
  reloadTablesFromDb(): void {
    this.database.getAllTables().subscribe({
      next: (tables) => {
        const tableObjects = tables.map(t => this.deserializeTable(t));
        this.tablesSubject.next(tableObjects);
      },
      error: (err) => console.error('Error reloading tables:', err)
    });
  }

  /** Recarga zonas desde la base de datos (p. ej. tras eliminar/inactivar zona). */
  reloadZonesFromDb(): void {
    this.database.getAllZones().subscribe({
      next: (zones) => {
        const zoneObjects = zones.map(z => this.deserializeZone(z));
        this.zonesSubject.next(zoneObjects);
      },
      error: (err) => console.error('Error reloading zones:', err)
    });
  }

  linkTables(primaryTableId: string, tableIdsToLink: string[]): void {
    const tables = this.getTables();
    const groupId = `group_${primaryTableId}_${Date.now()}`;

    const primaryTable = tables.find(t => t.id === primaryTableId);
    if (primaryTable) {
      primaryTable.groupId = groupId;
      primaryTable.linkedTables = tableIdsToLink;
      this.updateTable(primaryTable);
    }

    tableIdsToLink.forEach(tableId => {
      const table = tables.find(t => t.id === tableId);
      if (table) {
        table.groupId = groupId;
        table.linkedTables = [primaryTableId, ...tableIdsToLink.filter(id => id !== tableId)];
        this.updateTable(table);
      }
    });
  }

  unlinkTables(groupId: string): void {
    const tables = this.getTables();
    tables.forEach(table => {
      if (table.groupId === groupId) {
        table.groupId = undefined;
        table.linkedTables = undefined;
        table.status = TableStatus.AVAILABLE;
        this.updateTable(table);
      }
    });
  }

  getLinkedTables(tableId: string): Table[] {
    const table = this.getTableById(tableId);
    if (!table || !table.groupId) {
      return [];
    }
    return this.getTables().filter(t => t.groupId === table.groupId && t.id !== tableId);
  }

  getZones(): Zone[] {
    return this.zonesSubject.value.filter(z => z.active !== false);
  }

  /** Todas las zonas (incluidas inactivas) para lógica interna. */
  getAllZonesRaw(): Zone[] {
    return this.zonesSubject.value;
  }

  getZoneById(id: string): Zone | undefined {
    return this.getZones().find(zone => zone.id === id);
  }

  createZone(zone: Zone): Zone {
    const serialized = this.serializeZone(zone);
    this.database.createZone(serialized).subscribe({
      next: () => {
        const zones = [...this.getZones(), zone];
        this.zonesSubject.next(zones);
      },
      error: (error) => {
        console.error('Error creating zone:', error);
      }
    });
    return zone;
  }

  updateZone(zone: Zone): Zone {
    const serialized = this.serializeZone(zone);
    this.database.updateZone(serialized).subscribe({
      next: () => {
        const zones = this.getZones();
        const index = zones.findIndex(z => z.id === zone.id);
        if (index !== -1) {
          zones[index] = zone;
          this.zonesSubject.next([...zones]);
        }
      },
      error: (error) => {
        console.error('Error updating zone:', error);
      }
    });
    return zone;
  }

  /**
   * Elimina la zona y todas sus mesas (solo si ninguna mesa tiene pedidos en el historial).
   * Debe usarse desde el componente tras confirmar; si hay pedidos, usar inactivateZone.
   */
  deleteZone(id: string): void {
    const tablesInZone = this.getAllTablesRaw().filter(t => t.zoneId === id);
    tablesInZone.forEach(table => this.deleteTable(table.id));
    this.database.deleteZone(id).subscribe({
      next: () => {
        const zones = this.getAllZonesRaw().filter(z => z.id !== id);
        this.zonesSubject.next(zones);
        this.reloadZonesFromDb();
      },
      error: (error) => {
        console.error('Error deleting zone:', error);
      }
    });
  }

  /**
   * Inactiva la zona y todas sus mesas (para historial de pedidos).
   * No borra datos; pone active = false.
   */
  inactivateZone(id: string): void {
    const zones = this.getAllZonesRaw();
    const zone = zones.find(z => z.id === id);
    if (!zone) return;
    zone.active = false;
    this.updateZone(zone);
    const tablesInZone = this.getAllTablesRaw().filter(t => t.zoneId === id);
    tablesInZone.forEach(table => {
      table.active = false;
      table.updatedAt = new Date();
      this.updateTable(table);
    });
    setTimeout(() => {
      this.reloadZonesFromDb();
      this.reloadTablesFromDb();
    }, 200);
  }

  getLocations(): Location[] {
    return this.locationsSubject.value.filter(l => l.active !== false);
  }

  getSelectedLocationId(): string | null {
    return this.selectedLocationIdSubject.value;
  }

  setSelectedLocationId(locationId: string | null): void {
    this.selectedLocationIdSubject.next(locationId);
  }

  createLocation(location: Location): void {
    const serialized = this.serializeLocation(location);
    this.database.createLocation(serialized).subscribe({
      next: () => {
        const locations = [...this.locationsSubject.value, location];
        this.locationsSubject.next(locations);
        if (!this.selectedLocationIdSubject.value) {
          this.selectedLocationIdSubject.next(location.id);
        }
      },
      error: (error) => console.error('Error creating location:', error)
    });
  }

  updateLocation(location: Location): void {
    const serialized = this.serializeLocation(location);
    this.database.updateLocation(serialized).subscribe({
      next: () => {
        const locations = this.locationsSubject.value;
        const index = locations.findIndex(l => l.id === location.id);
        if (index !== -1) {
          locations[index] = location;
          this.locationsSubject.next([...locations]);
        }
      },
      error: (error) => console.error('Error updating location:', error)
    });
  }

  resetData(): void {
    // Este método se usa para resetear, pero los datos ahora están en IndexedDB
    // Se debe llamar desde DataInitService.clearAll()
    this.tablesSubject.next([]);
    this.zonesSubject.next([]);
  }
}
